import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { retryUnlessRecordUnavailable } from "@/common/utils/record-unavailable-error";
import { useTRPC } from "@/trpc";
import { isConflictError } from "../monitoring-format";

const STALE_ADDRESS_MESSAGE =
  "Someone else changed the report address. Showing the latest.";

const emailSchema = z
  .string()
  .trim()
  .min(1, "Enter an email address")
  .email("Enter a valid email address");

/** Where the monthly results for every provider also go, outside Saile. Hidden
 * from users restricted to specific pools, who can't see every provider. */
export function ReportAddressCard({
  organizationBusinessUnitId,
  canEdit,
}: {
  organizationBusinessUnitId: string;
  canEdit: boolean;
}) {
  const trpc = useTRPC();
  const [draft, setDraft] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const query = useQuery(
    trpc.psv.monitoring.getReportRecipient.queryOptions(
      { organizationBusinessUnitId },
      { retry: retryUnlessRecordUnavailable },
    ),
  );
  const save = useMutation(
    trpc.psv.monitoring.setReportRecipient.mutationOptions({
      onSuccess: async () => {
        await query.refetch();
        setDraft(null);
        toast.success("Report address saved");
      },
      onError: async (error) => {
        if (isConflictError(error)) {
          // The card refetches itself, so the server's "reload" advice
          // doesn't apply here.
          toast.error(STALE_ADDRESS_MESSAGE);
          await query.refetch();
          setDraft(null);
          return;
        }
        toast.error("Couldn't save the report address", {
          description: error.message,
        });
      },
    }),
  );

  // Pool-restricted users get FORBIDDEN; any load failure hides the card too.
  if (query.isPending || query.isError) {
    return null;
  }
  const recipient = query.data;
  const expectedRevision = recipient?.revision ?? null;

  function submit({ email }: { email: string | null }) {
    save.mutate({
      organizationBusinessUnitId,
      email,
      expectedRevision,
    });
  }

  function submitDraft() {
    const parsed = emailSchema.safeParse(draft ?? "");
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0].message);
      return;
    }
    setValidationError(null);
    submit({ email: parsed.data });
  }

  function cancelEditing() {
    setDraft(null);
    setValidationError(null);
  }

  return (
    <Card>
      <CardHeader>
        {/* Smaller than the "Monthly monitoring" section heading it sits under. */}
        <CardTitle className="text-lg">Report address</CardTitle>
        <CardDescription>
          Send the monthly results for every provider to an address outside
          Saile, such as a compliance inbox.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {draft === null ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className={recipient ? "font-medium" : "text-slate-600"}>
              {recipient?.email ?? "Not set"}
            </p>
            {canEdit && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDraft(recipient?.email ?? "")}
                >
                  {recipient ? "Change" : "Add address"}
                </Button>
                {recipient && (
                  <Button
                    variant="ghost"
                    disabled={save.isPending}
                    onClick={() => submit({ email: null })}
                  >
                    Remove
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : (
          <form
            className="space-y-3"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              submitDraft();
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="monitoring-report-address">Email address</Label>
              <Input
                id="monitoring-report-address"
                type="email"
                value={draft}
                aria-invalid={validationError !== null}
                aria-describedby={
                  validationError
                    ? "monitoring-report-address-error"
                    : undefined
                }
                onChange={(event) => setDraft(event.target.value)}
              />
              {validationError && (
                <p
                  id="monitoring-report-address-error"
                  role="alert"
                  className="text-sm text-destructive"
                >
                  {validationError}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={cancelEditing}>
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? "Saving..." : "Save address"}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
